import React, { useState, useEffect } from 'react';
import { Button } from '../ui';
import { 
  Play, 
  Square,
  Settings, 
  TrendingUp,
  RotateCcw,
  Zap,
  Wifi,
  WifiOff,
  Activity,
  Gauge,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import io from 'socket.io-client';
import { getApiUrl, getSocketUrl } from '../utils/api';

const BotControl: React.FC = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  
  // 호버 상태 관리
  const [isMainHovered, setIsMainHovered] = useState(false);
  const [isAnalysisHovered, setIsAnalysisHovered] = useState(false);
  const [isResetHovered, setIsResetHovered] = useState(false);

  // 로컬 상태로 봇 상태 관리 (임시)
  const [botState, setBotState] = useState({
    isRunning: true,
    status: 'running' as 'idle' | 'running' | 'paused' | 'error',
    profits: 2.4,
    trades: 156,
    winRate: 68.5,
    totalProfit: 1250.50,
    todayTrades: 12,
    avgProfit: 0.85
  });

  // 숫자 포맷팅 함수
  const formatNumber = (num: number, decimals: number = 0): string => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // 연결 상태 확인
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/health`);
        setConnectionStatus(response.ok ? 'connected' : 'disconnected');
      } catch (error) {
        setConnectionStatus('disconnected');
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 30000); // 30초마다 확인

    // WebSocket 연결 추가
    let socket: any = null;
    
    try {
      const serverUrl = getSocketUrl();
      
      socket = io(serverUrl, {
        transports: ['websocket', 'polling'],
        timeout: 10000,
        forceNew: true
      });

      socket.on('connect', () => {
        console.log('✅ 봇 제어 소켓 연결됨');
        setConnectionStatus('connected');
      });

      socket.on('disconnect', () => {
        console.log('❌ 봇 제어 소켓 연결 해제됨');
        setConnectionStatus('disconnected');
      });

      // 봇 상태 업데이트 수신
      socket.on('botStatusUpdate', (data: any) => {
        console.log('🤖 봇 제어 - 봇 상태 업데이트 수신:', data);
        setBotState(prev => ({
          ...prev,
          isRunning: data.isRunning,
          status: data.isRunning ? 'running' : 'paused'
        }));
      });

      socket.on('connect_error', (error: any) => {
        console.error('❌ 봇 제어 소켓 연결 오류:', error);
        setConnectionStatus('disconnected');
      });

    } catch (error) {
      console.error('❌ 봇 제어 소켓 초기화 오류:', error);
    }

    return () => {
      clearInterval(interval);
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  const executeInstantAnalysis = async () => {
    setIsAnalyzing(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/ai-analysis`);
      if (response.ok) {
        const analysisResult = await response.json();
        console.log('📊 즉시 분석 완료:', analysisResult);
        
        setTimeout(() => {
          setIsAnalyzing(false);
        }, 1000);
      } else {
        console.error('AI 분석 실패');
        setIsAnalyzing(false);
      }
    } catch (error) {
      console.error('즉시 분석 오류:', error);
      setIsAnalyzing(false);
    }
  };

  const resetBot = async () => {
    try {
      await fetch(`${getApiUrl()}/api/reset`, { method: 'POST' });
      console.log('🔄 봇 리셋 완료');
    } catch (error) {
      console.error('봇 리셋 오류:', error);
    }
  };

  const toggleBot = async () => {
    try {
      const endpoint = botState.isRunning ? '/api/bot/stop' : '/api/bot/start';
      console.log(`🤖 봇 ${botState.isRunning ? '정지' : '시작'} 요청`);
      
      const response = await fetch(`${getApiUrl()}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`서버 응답 오류: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ 봇 제어 응답:', data);
      
      // 서버 응답이 성공적이면 상태 업데이트
      setBotState(prev => ({
        ...prev,
        isRunning: !prev.isRunning,
        status: !prev.isRunning ? 'running' : 'paused'
      }));
      
      // 성공 알림 (선택적)
      console.log(`✅ 봇이 성공적으로 ${botState.isRunning ? '정지' : '시작'}되었습니다!`);
      
    } catch (error) {
      console.error('❌ 봇 제어 오류:', error);
      
      // 오류 발생 시 상태를 오류로 설정
      setBotState(prev => ({
        ...prev,
        status: 'error'
      }));
      
      // 사용자에게 오류 알림
      alert('봇 제어에 실패했습니다. 콘솔에서 세부 정보를 확인하세요.');
    }
  };

  const getStatusIcon = () => {
    switch (botState.status) {
      case 'running':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'paused':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Settings className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = () => {
    switch (botState.status) {
      case 'running':
        return '실행 중';
      case 'paused':
        return '정지됨';
      case 'error':
        return '오류';
      default:
        return '대기 중';
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center" 
            style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
          <div className="relative mr-3">
            <Settings className="h-7 w-7 text-blue-600 dark:text-blue-400" />
            {botState.isRunning && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg"></div>
            )}
          </div>
          🤖 봇 제어
        </h3>
        <div className="flex items-center space-x-2">
          {connectionStatus === 'connected' ? (
            <Wifi className="h-5 w-5 text-green-500" />
          ) : (
            <WifiOff className="h-5 w-5 text-red-500" />
          )}
        </div>
      </div>

      <div className="flex-1 space-y-5">
        {/* Bot Status */}
        <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-800 dark:via-gray-800 dark:to-blue-900/20 rounded-3xl p-5 border border-slate-200 dark:border-gray-600 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-gray-700 dark:text-gray-300 flex items-center"
                  style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
              {getStatusIcon()}
              <span className="ml-3">봇 상태</span>
            </span>
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${botState.isRunning ? 'bg-green-500 animate-pulse' : 'bg-red-500'} shadow-lg`} 
                   style={{ boxShadow: `0 0 15px ${botState.isRunning ? '#22c55e' : '#ef4444'}` }} />
              <span className={`text-lg font-bold ${botState.isRunning ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                    style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                {getStatusText()}
              </span>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-700 rounded-2xl p-4 border border-slate-200 dark:border-gray-600 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <Activity className="h-5 w-5 text-blue-500" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">가동률</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1"
                 style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
              98%
            </div>
            <div className="text-xs text-green-600 dark:text-green-400 flex items-center">
              <TrendingUp className="h-3 w-3 mr-1" />
              +0.5%
            </div>
          </div>

          <div className="bg-white dark:bg-gray-700 rounded-2xl p-4 border border-slate-200 dark:border-gray-600 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <DollarSign className="h-5 w-5 text-green-500" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">수익률</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1"
                 style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
              +{formatNumber(botState.profits, 1)}%
            </div>
            <div className="text-xs text-green-600 dark:text-green-400 flex items-center">
              <TrendingUp className="h-3 w-3 mr-1" />
              ${formatNumber(botState.totalProfit, 2)}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-700 rounded-2xl p-4 border border-slate-200 dark:border-gray-600 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <Gauge className="h-5 w-5 text-purple-500" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">총 거래</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1"
                 style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
              {formatNumber(botState.trades)}
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400">
              오늘 {formatNumber(botState.todayTrades)}건
            </div>
          </div>

          <div className="bg-white dark:bg-gray-700 rounded-2xl p-4 border border-slate-200 dark:border-gray-600 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <Target className="h-5 w-5 text-orange-500" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">승률</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1"
                 style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
              {formatNumber(botState.winRate, 1)}%
            </div>
            <div className="text-xs text-green-600 dark:text-green-400">
              평균 +{formatNumber(botState.avgProfit, 2)}%
            </div>
          </div>
        </div>

        {/* Control Buttons */}
        <div className="space-y-4">
          {/* Bot Toggle Button */}
          <Button
            onClick={toggleBot}
            onMouseEnter={() => setIsMainHovered(true)}
            onMouseLeave={() => setIsMainHovered(false)}
            className={`w-full flex items-center justify-center space-x-3 py-5 px-6 rounded-2xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl ${
              botState.isRunning
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
            style={{ 
              fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              backgroundColor: botState.isRunning 
                ? (isMainHovered ? 'rgb(185 28 28)' : 'rgb(220 38 38)')
                : (isMainHovered ? 'rgb(21 128 61)' : 'rgb(22 163 74)'),
              color: 'white',
              transform: isMainHovered ? 'translateY(-1px)' : 'translateY(0)',
              boxShadow: isMainHovered 
                ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            <div className="relative flex items-center space-x-3">
              {botState.isRunning ? (
                <>
                  <Square className="h-6 w-6" />
                  <span className="text-lg font-bold">봇 정지</span>
                </>
              ) : (
                <>
                  <Play className="h-6 w-6" />
                  <span className="text-lg font-bold">봇 시작</span>
                </>
              )}
            </div>
          </Button>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={executeInstantAnalysis}
              onMouseEnter={() => setIsAnalysisHovered(true)}
              onMouseLeave={() => setIsAnalysisHovered(false)}
              disabled={isAnalyzing}
              className={`flex items-center justify-center space-x-2 py-4 px-5 rounded-xl font-semibold transition-all duration-300 ${
                isAnalyzing
                  ? 'bg-blue-300 text-blue-800 cursor-not-allowed opacity-75'
                  : 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg'
              }`}
              style={{ 
                fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                backgroundColor: isAnalyzing 
                  ? 'rgb(147 197 253)' 
                  : (isAnalysisHovered ? 'rgb(37 99 235)' : 'rgb(59 130 246)'),
                color: isAnalyzing ? 'rgb(30 64 175)' : 'white',
                transform: !isAnalyzing && isAnalysisHovered ? 'translateY(-1px)' : 'translateY(0)',
                boxShadow: !isAnalyzing && isAnalysisHovered
                  ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                  : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
            >
              {isAnalyzing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-800"></div>
                  <span className="text-sm font-bold">분석중</span>
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  <span className="text-sm font-bold">즉시분석</span>
                </>
              )}
            </Button>

            <Button
              onClick={resetBot}
              onMouseEnter={() => setIsResetHovered(true)}
              onMouseLeave={() => setIsResetHovered(false)}
              className="flex items-center justify-center space-x-2 py-4 px-5 rounded-xl font-semibold transition-all duration-300 bg-orange-500 hover:bg-orange-600 text-white shadow-md hover:shadow-lg"
              style={{ 
                fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                backgroundColor: isResetHovered ? 'rgb(234 88 12)' : 'rgb(249 115 22)',
                color: 'white',
                transform: isResetHovered ? 'translateY(-1px)' : 'translateY(0)',
                boxShadow: isResetHovered
                  ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                  : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
            >
              <RotateCcw className="h-4 w-4" />
              <span className="text-sm font-bold">리셋</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotControl; 